import { Service } from "@/types/dashboard";
import { Dialog } from "@/components/ui/dialog";
import { DialogContentWithoutCloseButton } from "@/components/ui/dialog-no-close";
import { Button } from "@/components/ui/button";
import { ServiceCard } from "@/components/service-page/ServiceCard";
import { 
  Carousel, 
  CarouselContent, 
  CarouselItem, 
  CarouselNext, 
  CarouselPrevious 
} from "@/components/ui/carousel";
import { useState, useEffect } from "react";
import { formatCurrency } from "@/utils/analyticsUtils";

interface ServicePreviewDialogProps {
  service: Service | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceImages: any[] | undefined;
  selectedImageIndex: number;
  onImageNavigation: (direction: 'prev' | 'next') => void;
  businessName: string;
  currency?: string | null;
}

export const ServicePreviewDialog = ({
  service,
  open,
  onOpenChange,
  serviceImages = [],
  selectedImageIndex,
  onImageNavigation,
  businessName,
  currency
}: ServicePreviewDialogProps) => {
  const [api, setApi] = useState<any>(null);
  const [current, setCurrent] = useState(0);
  const [loadedImages, setLoadedImages] = useState<{[key: string]: boolean}>({});

  useEffect(() => {
    if (serviceImages && serviceImages.length > 0) {
      const initialLoadState: {[key: string]: boolean} = {};
      serviceImages.forEach((img, index) => {
        const imageId = img.id || `${img.image_url}-${index}`;
        initialLoadState[imageId] = false;
      });
      setLoadedImages(initialLoadState);
    }
  }, [serviceImages]);

  useEffect(() => {
    setCurrent(selectedImageIndex);
    
    if (api) {
      api.scrollTo(selectedImageIndex);
    }
  }, [selectedImageIndex, api]);

  const handleSelect = () => {
    if (!api) return;
    
    const newIndex = api.selectedScrollSnap();
    setCurrent(newIndex);
    
    // Only trigger navigation if the index actually changed
    if (newIndex !== current) {
      onImageNavigation(newIndex < current ? 'prev' : 'next');
    }
  };

  const handleImageLoad = (imageId: string) => {
    setLoadedImages(prev => ({
      ...prev,
      [imageId]: true
    }));
  };

  if (!service) return null;

  const hasImages = serviceImages && serviceImages.length > 0;
  const hasMultipleImages = serviceImages && serviceImages.length > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContentWithoutCloseButton className="max-w-[800px] sm:max-w-[800px] p-0 bg-white dark:bg-gray-900 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4 overflow-y-auto">
          {hasImages && (
            <div className="relative aspect-square overflow-hidden bg-gray-100 rounded-md">
              {hasMultipleImages ? (
                <Carousel
                  setApi={setApi}
                  opts={{
                    align: 'start',
                    loop: true,
                    dragFree: false,
                    containScroll: "trimSnaps",
                  }}
                  className="w-full h-full touch-pan-y"
                  onSelect={handleSelect}
                >
                  <CarouselContent className="h-full">
                    {serviceImages.map((image, index) => {
                      const imageId = image.id || `${image.image_url}-${index}`;
                      const isLoaded = loadedImages[imageId];
                      
                      return (
                        <CarouselItem key={imageId} className="h-full">
                          <div className="h-full w-full flex items-center justify-center">
                            <div className={`w-full h-full bg-gray-200 absolute ${isLoaded ? 'hidden' : 'flex items-center justify-center'}`}>
                              <div className="w-10 h-10 border-4 border-gebeya-pink border-t-transparent rounded-full animate-spin"></div>
                            </div>
                            <img
                              src={image.image_url}
                              alt={`${service.name} - Image ${index + 1}`}
                              className={`w-full h-full object-cover object-center ${isLoaded ? '' : 'opacity-0'}`}
                              onLoad={() => handleImageLoad(imageId)}
                              style={{ aspectRatio: '1/1' }}
                            />
                          </div>
                        </CarouselItem>
                      );
                    })}
                  </CarouselContent>
                  
                  {/* Custom nav buttons */}
                  <CarouselPrevious className="left-2 bg-black/50 text-white hover:bg-black/70" />
                  <CarouselNext className="right-2 bg-black/50 text-white hover:bg-black/70" />
                </Carousel>
              ) : (
                <div className="h-full w-full flex items-center justify-center">
                  <img
                    src={serviceImages[0].image_url}
                    alt={`${service.name} - Image 1`}
                    className="w-full h-full object-cover object-center"
                    onLoad={() => handleImageLoad(serviceImages[0].id || `${serviceImages[0].image_url}-0`)}
                    style={{ aspectRatio: '1/1' }}
                  />
                </div>
              )}
            </div>
          )}
          
          <div className={!hasImages ? "md:col-span-2" : ""}>
            <h2 className="text-2xl font-bold mb-2 break-words">{service.name}</h2>
            
            {businessName && (
              <p className="text-sm text-gray-500 mb-2">Offered by {businessName}</p>
            )}
            
            {service.description && (
              <div className="mb-4">
                <h3 className="text-lg font-medium mb-1">Description</h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{service.description}</p>
              </div>
            )}
            
            <div className="mb-2">
              <h3 className="text-lg font-medium">Price</h3>
              <div className="flex items-center">
                <span className="text-2xl font-semibold text-gebeya-pink">
                  {formatCurrency(
                    service.discounted_price && service.discounted_price > 0 
                      ? service.discounted_price 
                      : service.price,
                    currency || 'KES'
                  )}
                </span>
                
                {service.discounted_price && service.discounted_price > 0 && (
                  <span className="ml-2 text-sm text-gray-500 line-through">
                    {formatCurrency(service.price, currency || 'KES')}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-4 border-t flex justify-center sticky bottom-0 left-0 right-0 bg-white z-10">
          <Button 
            variant="outline"
            className="w-full max-w-xs"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </div>
      </DialogContentWithoutCloseButton>
    </Dialog>
  );
};